// =============================================================================
// Shared Calendar Utilities
// Single source of truth for all date/time calculations
// =============================================================================

// Days in each month (non-leap year, 1-indexed)
export const DAYS_IN_MONTH = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Check if a year is a leap year
 */
export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Get the number of days in a month
 */
export function getDaysInMonth(month: number, year?: number): number {
  if (month === 2 && year && isLeapYear(year)) {
    return 29;
  }
  return DAYS_IN_MONTH[month];
}

/**
 * Pad a number with leading zeros
 */
export function padNumber(n: number, length: number = 2): string {
  return n.toString().padStart(length, "0");
}

/**
 * Get the start and end dates of a month
 */
export function getMonthRange(month: number, year: number): { from: string; to: string } {
  const endDay = getDaysInMonth(month, year);

  return {
    from: `${year}-${padNumber(month)}-01`,
    to: `${year}-${padNumber(month)}-${padNumber(endDay)}`,
  };
}

/**
 * Get the start and end dates of a year
 */
export function getYearRange(year: number): { from: string; to: string } {
  return {
    from: `${year}-01-01`,
    to: `${year}-12-31`,
  };
}

/**
 * Get the start and end dates of a half-year
 */
export function getHalfYearRange(half: 1 | 2, year: number): { from: string; to: string } {
  if (half === 1) {
    return {
      from: `${year}-01-01`,
      to: `${year}-06-30`,
    };
  }
  return {
    from: `${year}-07-01`,
    to: `${year}-12-31`,
  };
}

/**
 * Validate date values
 */
export function validateDate(year: number | undefined, month: number, day: number): void {
  if (month < 1 || month > 12) {
    throw new Error(`Invalid month: ${month}`);
  }
  if (day < 1) {
    throw new Error(`Invalid day: ${day}`);
  }
  const maxDays = getDaysInMonth(month, year);
  if (day > maxDays) {
    if (month === 2 && day === 29) {
      throw new Error(`February 29 is invalid for year ${year} (not a leap year)`);
    }
    throw new Error(`Invalid day ${day} for month ${month}`);
  }
}

/**
 * Validate time values
 */
export function validateTime(hour: number, minute: number, second?: number): void {
  if (hour < 0 || hour > 23) {
    throw new Error(`Invalid hour: ${hour}`);
  }
  if (minute < 0 || minute > 59) {
    throw new Error(`Invalid minute: ${minute}`);
  }
  if (second !== undefined && (second < 0 || second > 59)) {
    throw new Error(`Invalid second: ${second}`);
  }
}

// =============================================================================
// Date Object Returning Functions
// These return Date objects for use in parsing/resolving
// =============================================================================

/**
 * Get the start and end Date objects of a month
 */
export function getMonthRangeDates(month: number, year: number): { from: Date; to: Date } {
  const endDay = getDaysInMonth(month, year);
  const from = new Date(Date.UTC(year, month - 1, 1, 0, 0, 0, 0));
  const to = new Date(Date.UTC(year, month - 1, endDay, 23, 59, 59, 999));
  return { from, to };
}

/**
 * Get the start and end Date objects of a year
 */
export function getYearRangeDates(year: number): { from: Date; to: Date } {
  const from = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
  const to = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
  return { from, to };
}

/**
 * Get the start and end Date objects of a half-year
 */
export function getHalfYearRangeDates(half: 1 | 2, year: number): { from: Date; to: Date } {
  if (half === 1) {
    const from = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
    const to = new Date(Date.UTC(year, 5, 30, 23, 59, 59, 999));
    return { from, to };
  }
  const from = new Date(Date.UTC(year, 6, 1, 0, 0, 0, 0));
  const to = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
  return { from, to };
}

// =============================================================================
// ISO Week Functions
// =============================================================================

/**
 * Get the ISO week number for a date
 * ISO weeks start on Monday, week 1 contains Jan 4
 */
export function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  // Set to nearest Thursday (current date + 4 - current day number)
  // Make Sunday's day number 7
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  // Calculate full weeks to nearest Thursday
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return weekNo;
}

/**
 * Get the ISO week year for a date
 * This can differ from calendar year at year boundaries
 */
export function getISOWeekYear(date: Date): number {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  return d.getUTCFullYear();
}

/**
 * Get the date of the first day (Monday) of an ISO week
 */
export function getStartOfISOWeek(year: number, week: number): Date {
  // Find Jan 4 of the year (always in week 1)
  const jan4 = new Date(Date.UTC(year, 0, 4));
  // Get the Monday of week 1
  const dayOfWeek = jan4.getUTCDay() || 7; // 1-7 where Monday is 1
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (dayOfWeek - 1));
  // Add (week - 1) * 7 days to get to the target week
  const targetMonday = new Date(week1Monday);
  targetMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  targetMonday.setUTCHours(0, 0, 0, 0);
  return targetMonday;
}

/**
 * Get the start and end Date objects of an ISO week
 */
export function getISOWeekRangeDates(year: number, week: number): { from: Date; to: Date } {
  // Validate week number (1-53)
  if (week < 1 || week > 53) {
    throw new Error(`Invalid ISO week: ${week}`);
  }

  const from = getStartOfISOWeek(year, week);
  const to = new Date(from);
  to.setUTCDate(from.getUTCDate() + 6); // Sunday
  to.setUTCHours(23, 59, 59, 999);

  return { from, to };
}

/**
 * Parse ISO week string (e.g., "2026-W02" or "2026W02")
 */
export function parseISOWeek(input: string): { year: number; week: number } | null {
  // Match "2026-W02" or "2026W02"
  const match = input.match(/^(\d{4})-?W(\d{2})$/i);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);

  // Validate week range
  if (week < 1 || week > 53) return null;

  return { year, week };
}

// =============================================================================
// Date Formatting Utilities
// =============================================================================

/**
 * Format a Date object to YYYY-MM-DD string
 * Uses UTC values to ensure consistent output regardless of local timezone
 */
export function formatDateToYMD(date: Date): string {
  const year = date.getUTCFullYear();
  const month = padNumber(date.getUTCMonth() + 1);
  const day = padNumber(date.getUTCDate());
  return `${year}-${month}-${day}`;
}

/**
 * Format a Date object to YYYY-MM-DD HH:mm string
 * Uses UTC values to ensure consistent output regardless of local timezone
 */
export function formatDateTimeToYMDHM(date: Date): string {
  const datePart = formatDateToYMD(date);
  const hours = padNumber(date.getUTCHours());
  const minutes = padNumber(date.getUTCMinutes());
  return `${datePart} ${hours}:${minutes}`;
}

/**
 * Format a Date object to YYYY-MM-DD HH:mm:ss string
 * Uses UTC values to ensure consistent output regardless of local timezone
 */
export function formatDateTimeToYMDHMS(date: Date): string {
  const datePart = formatDateToYMD(date);
  const hours = padNumber(date.getUTCHours());
  const minutes = padNumber(date.getUTCMinutes());
  const seconds = padNumber(date.getUTCSeconds());
  return `${datePart} ${hours}:${minutes}:${seconds}`;
}
